import cx from 'classnames';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Card, RequiredMark} from 'components';
import {TRANSLATION_KEYS} from 'locale';

import type {FunnelResult, VersionLabel} from './types';
import {preSessionFlowAnalyticsUtils} from './utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.PRE_SESSION_FLOW.FUNNEL;

type Props = {
    data?: FunnelResult[];
    versions: VersionLabel[];
    isLoading: boolean;
};

export const PreSessionFlowFunnelChart = ({data, versions, isLoading}: Props) => {
    const {t} = useTranslation();
    const labelMap = useMemo(() => preSessionFlowAnalyticsUtils.buildVersionLabelMaps(versions).stepNames, [versions]);

    if (isLoading) {
        return <Card><div className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.LOADING)}</div></Card>;
    }
    if (!data || data.length === 0) {
        return <Card><div className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.NO_DATA)}</div></Card>;
    }

    return (
        <Card>
            <h3 className='tw-ts-h5-bold tw-mb-3'>{t(T_KEYS.TITLE)}</h3>
            <div className='tw-flex tw-items-center tw-gap-3 tw-mb-2 tw-text-xs tw-text-default-dimmed'>
                <div className='tw-w-6 tw-flex-shrink-0' />
                <div className='tw-flex-1 tw-min-w-0'>{t(T_KEYS.STEP)}</div>
                <div className='tw-w-16 tw-text-right tw-flex-shrink-0'>{t(T_KEYS.REACHED)}</div>
                <div className='tw-w-20 tw-text-right tw-flex-shrink-0'>{t(T_KEYS.COMPLETION)}</div>
            </div>
            <div className='tw-flex tw-flex-col tw-gap-3'>
                {data.map((row, idx) => {
                    const name = labelMap.get(row.stepId) || `Step ${row.stepId.slice(0, 6)}`;
                    // Bar = completion rate (completed of those who reached this step). Reach varies
                    // across flow versions, so it's not a single sequential funnel — completion rate
                    // is the version-independent "where do clients get stuck" signal.
                    const completionPct = row.reached > 0 ? Math.round((row.completed / row.reached) * 100) : 0;
                    return (
                        <div key={row.stepId} className='tw-flex tw-items-center tw-gap-3'>
                            <div className='tw-w-6 tw-text-xs tw-text-default-dimmed tw-text-right tw-flex-shrink-0'>
                                {String(idx + 1).padStart(2, '0')}
                            </div>
                            <div className='tw-flex-1 tw-min-w-0'>
                                <div className='tw-flex tw-items-center tw-text-sm tw-mb-1'>
                                    <span className='tw-truncate'>{name}</span>
                                    {row.required && <RequiredMark />}
                                </div>
                                <div className='tw-h-2 tw-bg-layout tw-rounded tw-overflow-hidden'>
                                    <div
                                        className={cx('tw-h-full tw-transition-all', {
                                            'tw-bg-success': completionPct >= 66,
                                            'tw-bg-warning': completionPct < 66 && completionPct >= 33,
                                            'tw-bg-alert': completionPct < 33,
                                        })}
                                        style={{width: `${completionPct}%`}}
                                    />
                                </div>
                            </div>
                            <div className='tw-w-16 tw-text-right tw-text-sm tw-tabular-nums tw-flex-shrink-0'>
                                {row.reached.toLocaleString()}
                            </div>
                            <div className='tw-w-20 tw-text-right tw-text-sm tw-text-default-dimmed tw-tabular-nums tw-flex-shrink-0'>
                                {completionPct}
                                {'%'}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};
